import React, { useEffect, useState } from 'react';
import { ChoiceGroup, DefaultButton, IChoiceGroupOption, PrimaryButton, Stack, Text, TextField } from '@fluentui/react';
import IvicosStrings from 'kits/language/stringKit';
import { OrganizationDto, ZammadTicketConfigurationDto, ZammadTicketDelivery } from 'services/client-api-wrapper/identity_provider/responses.dto';
import { resolveZammadErrorMessage } from '../Zammad/ZammadSettingsForm';
import { IZammadTicketConfiguration, ZAMMAD_TICKET_MAX_CATEGORIES, ZAMMAD_TICKET_MAX_CATEGORY_LENGTH } from './zammadTicket.types';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HTTPS_URL_PATTERN = /^https:\/\/[^\s/]+/i;
// The form returns the real Zammad ticket number and makes the reporter the customer, with no
// Zammad mail settings to change; e-mail stays the fallback for campuses without a Zammad form.
const DEFAULT_DELIVERY: ZammadTicketDelivery = 'form';

/** Zammad is a single-page app, so a pasted address-bar URL often carries a path or #fragment; the
 *  widget appends /api/v1/form_config, so keep only the origin (the server does the same). */
const toOrigin = (value: string): string => {
    try {
        return new URL(value.trim()).origin;
    } catch {
        return value.trim();
    }
};

/** One category per line; blank lines and duplicates dropped — the server does the same. */
const parseCategories = (text: string): string[] =>
    Array.from(
        new Set(
            text
                .split('\n')
                .map((line) => line.trim())
                .filter((line) => line !== '')
        )
    );

interface IZammadTicketSettingsFormProps {
    configuration: ZammadTicketConfigurationDto | null;
    isOpen: boolean;
    updateZammadTicketConfig: (config: IZammadTicketConfiguration) => Promise<OrganizationDto>;
    onSaveSuccess: () => void;
    onCancel: () => void;
}

export const ZammadTicketSettingsForm: React.FC<IZammadTicketSettingsFormProps> = ({
    configuration,
    isOpen,
    updateZammadTicketConfig,
    onSaveSuccess,
    onCancel
}) => {
    const [delivery, setDelivery] = useState<ZammadTicketDelivery>(configuration?.delivery ?? DEFAULT_DELIVERY);
    const [targetEmail, setTargetEmail] = useState(configuration?.target_email ?? '');
    const [formUrl, setFormUrl] = useState(configuration?.zammad_form_url ?? '');
    const [categoriesText, setCategoriesText] = useState((configuration?.categories ?? []).join('\n'));
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // The Panel keeps this form mounted while closed, so re-sync from the saved configuration on
    // every open/close — an unsaved draft must never survive Cancel.
    useEffect(() => {
        setDelivery(configuration?.delivery ?? DEFAULT_DELIVERY);
        setTargetEmail(configuration?.target_email ?? '');
        setFormUrl(configuration?.zammad_form_url ?? '');
        setCategoriesText((configuration?.categories ?? []).join('\n'));
        setErrorMessage(null);
        setIsSaving(false);
    }, [isOpen, configuration]);

    const deliveryOptions: IChoiceGroupOption[] = [
        { key: 'form', text: IvicosStrings.zammadTicketSettingsDeliveryForm },
        { key: 'email', text: IvicosStrings.zammadTicketSettingsDeliveryEmail }
    ];

    const validate = (categories: string[]): string | null => {
        if (delivery === 'email' && !EMAIL_PATTERN.test(targetEmail.trim())) return IvicosStrings.zammadTicketSettingsInvalidEmail;
        if (delivery === 'form' && !HTTPS_URL_PATTERN.test(formUrl.trim())) return IvicosStrings.zammadTicketSettingsInvalidUrl;
        if (categories.length > ZAMMAD_TICKET_MAX_CATEGORIES || categories.some((category) => category.length > ZAMMAD_TICKET_MAX_CATEGORY_LENGTH)) {
            return IvicosStrings.zammadTicketSettingsInvalidCategories;
        }
        return null;
    };

    const handleSave = async (): Promise<void> => {
        const categories = parseCategories(categoriesText);
        const validationError = validate(categories);
        if (validationError) {
            setErrorMessage(validationError);
            return;
        }

        setIsSaving(true);
        setErrorMessage(null);
        try {
            await updateZammadTicketConfig({
                is_zammad_ticket_enable: true,
                delivery,
                target_email: delivery === 'email' ? targetEmail.trim() : null,
                zammad_form_url: delivery === 'form' ? toOrigin(formUrl) : null,
                categories
            });
            onSaveSuccess();
        } catch (error) {
            setErrorMessage(resolveZammadErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Stack verticalAlign="space-between" styles={{ root: { height: '100%', minHeight: 'calc(100vh - 110px)' } }}>
            <Stack tokens={{ childrenGap: 16 }} style={{ marginTop: 16 }}>
                <ChoiceGroup
                    label={IvicosStrings.zammadTicketSettingsDeliveryLabel}
                    options={deliveryOptions}
                    selectedKey={delivery}
                    onChange={(_ev, option) => option && setDelivery(option.key as ZammadTicketDelivery)}
                    data-test-id="zammad-ticket-settings-delivery"
                />
                {delivery === 'email' ? (
                    <Stack tokens={{ childrenGap: 4 }}>
                        <TextField
                            label={IvicosStrings.zammadTicketSettingsTargetEmailLabel}
                            type="email"
                            value={targetEmail}
                            onChange={(_ev, value) => setTargetEmail(value ?? '')}
                            data-test-id="zammad-ticket-settings-target-email"
                        />
                        <Text variant="small">{IvicosStrings.zammadTicketSettingsEmailHint}</Text>
                    </Stack>
                ) : (
                    <Stack tokens={{ childrenGap: 4 }}>
                        <TextField
                            label={IvicosStrings.zammadTicketSettingsFormUrlLabel}
                            type="url"
                            value={formUrl}
                            onChange={(_ev, value) => setFormUrl(value ?? '')}
                            data-test-id="zammad-ticket-settings-form-url"
                        />
                        <Text variant="small">{IvicosStrings.zammadTicketSettingsFormHint}</Text>
                    </Stack>
                )}
                <TextField
                    label={IvicosStrings.zammadTicketSettingsCategoriesLabel}
                    multiline
                    rows={5}
                    value={categoriesText}
                    onChange={(_ev, value) => setCategoriesText(value ?? '')}
                    data-test-id="zammad-ticket-settings-categories"
                />
                {errorMessage && (
                    <Text style={{ color: '#A80000' }} data-test-id="zammad-ticket-settings-error">
                        {errorMessage}
                    </Text>
                )}
            </Stack>
            <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { width: '100%', paddingBottom: 8 } }}>
                <Stack.Item grow styles={{ root: { flexBasis: 0 } }}>
                    <DefaultButton text={IvicosStrings.zammadSettingsCancelButton} onClick={onCancel} styles={{ root: { width: '100%' } }} />
                </Stack.Item>
                <Stack.Item grow styles={{ root: { flexBasis: 0 } }}>
                    <PrimaryButton
                        text={IvicosStrings.zammadSettingsSaveButton}
                        onClick={handleSave}
                        disabled={isSaving}
                        styles={{ root: { width: '100%' } }}
                        data-test-id="zammad-ticket-settings-save-button"
                    />
                </Stack.Item>
            </Stack>
        </Stack>
    );
};
